import type { CardDef } from '../types.js';
import type { PlayerData } from '../collection/PlayerState.js';
import type { MenuAction } from './MenuView.js';
import { apiClient } from '../api-client.js';

// Settings emits ordinary menu actions only — profile-card changes
// sync `playerData.profileCardId` in place, and the main menu picks
// up the new value on the next render when the player navigates back.
// No bespoke event needed for the v1 surface.
export type SettingsAction = MenuAction;

/** Account-settings screen. Shows:
 *
 *    - account meta (username, created_at, guest badge)
 *    - rename / password forms
 *    - profile-card picker (carousel of all owned cards, alpha-sorted,
 *      with a name filter input; per-tile art loads through the
 *      concurrency-capped ImageLoader so a 1000-card collection
 *      doesn't flood the network on first paint or fast scroll)
 *
 *  Guests see a stripped-down version explaining they need to register
 *  before they can mutate settings — same logic as the custom-pack
 *  account gate.
 *
 *  Returns a teardown for any DOM listeners we set on `document` /
 *  `window` (currently none, but kept for symmetry with the other
 *  views' lifecycle pattern). */
export function createSettingsView(
  container: HTMLElement,
  playerData: PlayerData,
  catalog: Map<string, CardDef>,
  onAction: (action: SettingsAction) => void,
): () => void {
  container.innerHTML = '';
  const el = document.createElement('div');
  el.className = 'menu-screen settings-screen is-scrollable';

  if (playerData.isGuest) {
    el.innerHTML = `
      <button class="menu-btn campaign-back" id="back-btn">&larr; Back</button>
      <h1 class="menu-title">Settings</h1>
      <div class="settings-body">
        <div class="settings-section">
          <p>You're playing as a guest. Create an account to set a username, password, and profile picture.</p>
          <div class="settings-actions-row">
            <button class="menu-btn primary" id="register-btn">Create Account</button>
            <button class="menu-btn" id="sign-in-btn">Sign In</button>
          </div>
        </div>
      </div>
    `;
    container.appendChild(el);
    el.querySelector('#back-btn')!.addEventListener('click', () => onAction({ type: 'back-to-menu' }));
    el.querySelector('#register-btn')!.addEventListener('click', () => onAction({ type: 'register' }));
    el.querySelector('#sign-in-btn')!.addEventListener('click', () => onAction({ type: 'sign-in' }));
    return () => {};
  }

  el.innerHTML = `
    <button class="menu-btn campaign-back" id="back-btn">&larr; Back</button>
    <h1 class="menu-title">Settings</h1>
    <div class="settings-body">
      <section class="settings-section">
        <h2 class="section-title">Account</h2>
        <div class="settings-meta">
          <div><span class="settings-meta-label">Username:</span> <strong id="username-meta">${escapeHtml(playerData.username ?? '')}</strong></div>
          <div><span class="settings-meta-label">Member since:</span> ${escapeHtml(formatDate(playerData.createdAt))}</div>
        </div>
      </section>

      <section class="settings-section">
        <h2 class="section-title">Change username</h2>
        <form id="username-form" class="inline-form">
          <input type="text" id="username-input" minlength="3" maxlength="30" value="${escapeHtml(playerData.username ?? '')}" required />
          <button type="submit" class="menu-btn" id="username-save">Save</button>
        </form>
        <div class="settings-feedback" id="username-feedback"></div>
      </section>

      <section class="settings-section">
        <h2 class="section-title">Change password</h2>
        <form id="password-form" class="settings-stack">
          <input type="password" id="cur-pw" placeholder="Current password" autocomplete="current-password" minlength="6" required />
          <input type="password" id="new-pw" placeholder="New password (≥ 6 chars)" autocomplete="new-password" minlength="6" required />
          <button type="submit" class="menu-btn">Save</button>
        </form>
        <div class="settings-feedback" id="password-feedback"></div>
      </section>

      <section class="settings-section">
        <h2 class="section-title">Profile picture</h2>
        <div class="profile-section-row">
          <div class="profile-current-preview" id="profile-current-preview" role="button" tabindex="0" title="Tap to view card details">
            ${renderCurrentArt(playerData.profileCardId, catalog)}
          </div>
          <div class="profile-section-side">
            <div class="profile-section-name">
              <strong id="current-profile-name">${escapeHtml(profileNameFor(playerData.profileCardId, catalog))}</strong>
            </div>
            <button type="button" class="menu-btn" id="open-profile-picker">Change</button>
          </div>
        </div>
        <div class="settings-feedback" id="profile-feedback"></div>
      </section>
    </div>
  `;
  container.appendChild(el);

  el.querySelector('#back-btn')!.addEventListener('click', () => onAction({ type: 'back-to-menu' }));

  // ── Username ────────────────────────────────────────────────
  const usernameInput = el.querySelector('#username-input') as HTMLInputElement;
  const usernameFeedback = el.querySelector('#username-feedback') as HTMLDivElement;
  const usernameSave = el.querySelector('#username-save') as HTMLButtonElement;
  const usernameMeta = el.querySelector('#username-meta') as HTMLElement;

  /** Apply / clear the cooldown lockout on the rename form based on
   *  `playerData.usernameChangeAvailableAt`. Disables the input and
   *  the Save button when an active cooldown exists, and shows a
   *  neutral "next change available in …" line so the player knows
   *  the form isn't broken. Called on initial render and after a
   *  successful rename (which triggers a fresh cooldown). */
  function applyUsernameCooldown(): void {
    const nextAt = playerData.usernameChangeAvailableAt;
    const remainingMs = nextAt ? new Date(nextAt).getTime() - Date.now() : 0;
    if (remainingMs > 0) {
      usernameInput.disabled = true;
      usernameSave.disabled = true;
      usernameFeedback.textContent = `Next change available in ${formatDurationApprox(remainingMs)}.`;
      usernameFeedback.className = 'settings-feedback';
    } else {
      usernameInput.disabled = false;
      usernameSave.disabled = false;
      // Don't blow away success / error messages; only clear if the
      // previous text was a cooldown notice.
      if (usernameFeedback.textContent?.startsWith('Next change available')) {
        usernameFeedback.textContent = '';
      }
    }
  }
  applyUsernameCooldown();

  el.querySelector('#username-form')!.addEventListener('submit', async (e) => {
    e.preventDefault();
    const next = usernameInput.value.trim();
    usernameFeedback.textContent = '';
    try {
      const profile = await apiClient.setUsername(next);
      playerData.username = profile.username;
      playerData.usernameChangeAvailableAt = profile.usernameChangeAvailableAt ?? null;
      // Patch the meta line so it reflects the new name without a
      // full view rerender — the previous code only updated the
      // input value, leaving the "Username: oldname" line stale.
      if (usernameMeta) usernameMeta.textContent = profile.username ?? '';
      usernameInput.value = profile.username ?? '';
      usernameFeedback.textContent = 'Saved.';
      usernameFeedback.className = 'settings-feedback success';
      // Disable the form for the new cooldown window.
      applyUsernameCooldown();
    } catch (err) {
      usernameFeedback.textContent = err instanceof Error ? err.message : String(err);
      usernameFeedback.className = 'settings-feedback error';
    }
  });

  // ── Password ───────────────────────────────────────────────
  const curPwInput = el.querySelector('#cur-pw') as HTMLInputElement;
  const newPwInput = el.querySelector('#new-pw') as HTMLInputElement;
  const passwordFeedback = el.querySelector('#password-feedback') as HTMLDivElement;
  el.querySelector('#password-form')!.addEventListener('submit', async (e) => {
    e.preventDefault();
    passwordFeedback.textContent = '';
    try {
      await apiClient.setPassword(curPwInput.value, newPwInput.value);
      curPwInput.value = '';
      newPwInput.value = '';
      passwordFeedback.textContent = 'Password updated.';
      passwordFeedback.className = 'settings-feedback success';
    } catch (err) {
      passwordFeedback.textContent = err instanceof Error ? err.message : String(err);
      passwordFeedback.className = 'settings-feedback error';
    }
  });

  // ── Profile picture (preview + Change button) ──────────────
  //
  // Top-level settings panel shows the current art + a Change button.
  // Tapping the art opens the standard card-detail modal so the
  // player can read the card's full info. Tapping Change opens the
  // picker modal (see openProfilePicker below) — picking a new card
  // doesn't persist until the modal's Save button is pressed, so a
  // player can scroll the picker, compare options, and back out
  // with Cancel without affecting their stored profile.
  const profileFeedback = el.querySelector('#profile-feedback') as HTMLDivElement;
  const currentNameEl = el.querySelector('#current-profile-name') as HTMLElement;
  const currentPreviewEl = el.querySelector('#profile-current-preview') as HTMLElement;
  const openPickerBtn = el.querySelector('#open-profile-picker') as HTMLButtonElement;

  // Snapshot owned cards once. Used by the picker modal.
  const allOwned: CardDef[] = Object.entries(playerData.collection)
    .filter(([, count]) => count > 0)
    .map(([id]) => catalog.get(id))
    .filter((d): d is CardDef => d != null)
    .sort((a, b) => a.name.localeCompare(b.name));

  /** Tap-to-detail on the current-pic preview. Pulled into its own
   *  function because the preview's innerHTML gets rewritten when
   *  the saved selection changes, but the click handler on the
   *  outer div persists (we attach below the innerHTML rewrites). */
  function openCurrentDetail(): void {
    if (playerData.profileCardId) {
      // Lazy import via dynamic require would be ideal but is
      // awkward in TS — just a normal import does the right thing
      // since CardDetailView is small.
      void import('./CardDetailView.js').then(m => {
        if (playerData.profileCardId) m.showCardDetail(playerData.profileCardId, catalog);
      });
    }
  }
  currentPreviewEl.addEventListener('click', openCurrentDetail);
  currentPreviewEl.addEventListener('keydown', (e) => {
    if ((e as KeyboardEvent).key === 'Enter' || (e as KeyboardEvent).key === ' ') {
      e.preventDefault();
      openCurrentDetail();
    }
  });

  openPickerBtn.addEventListener('click', async () => {
    const picked = await openProfilePicker(allOwned, playerData.profileCardId, catalog);
    if (picked == null || picked === playerData.profileCardId) return; // cancel or no change
    profileFeedback.textContent = '';
    try {
      const profile = await apiClient.setProfileCard(picked);
      playerData.profileCardId = profile.profileCardId ?? null;
      const def = catalog.get(picked);
      currentNameEl.textContent = def?.name ?? '';
      currentPreviewEl.innerHTML = renderCurrentArt(playerData.profileCardId, catalog);
      profileFeedback.textContent = 'Profile picture updated.';
      profileFeedback.className = 'settings-feedback success';
    } catch (err) {
      profileFeedback.textContent = err instanceof Error ? err.message : String(err);
      profileFeedback.className = 'settings-feedback error';
    }
  });

  // No long-lived observers anymore — the picker modal owns its
  // own IntersectionObserver lifecycle while it's open.
  return () => {};
}

/** Full-screen picker modal for the profile picture. Returns the
 *  chosen card id, or null if the player cancels. "No change"
 *  (Save pressed but selection unchanged) also resolves with the
 *  original id; the settings caller no-ops when picked === current.
 *
 *  Layout:
 *    - Fixed top: filter input + currently-highlighted preview
 *      (tap to open card detail). Both pinned so they stay visible
 *      regardless of how far the carousel has been scrolled.
 *    - Scrollable middle: avatar grid, using the same hydrate /
 *      dehydrate IntersectionObserver pattern as the deck-builder
 *      grid so a 1000-card collection doesn't flood the network.
 *    - Fixed bottom: Save + Cancel.
 *
 *  The "selected" highlight follows the player's taps locally and
 *  is only persisted by the caller after Save resolves.
 */
function openProfilePicker(
  allOwned: CardDef[],
  initialCardId: string | null,
  catalog: Map<string, CardDef>,
): Promise<string | null> {
  return new Promise((resolve) => {
    let selected: string | null = initialCardId;
    let filterText = '';

    const overlay = document.createElement('div');
    overlay.className = 'profile-picker-overlay';
    overlay.innerHTML = `
      <div class="profile-picker-modal">
        <header class="profile-picker-header">
          <div class="profile-picker-preview" id="picker-preview" role="button" tabindex="0" title="Tap to view card details">
            ${renderCurrentArt(selected, catalog)}
          </div>
          <div class="profile-picker-meta">
            <div class="profile-picker-name" id="picker-name">${escapeHtml(initialCardId ? catalog.get(initialCardId)?.name ?? '' : '(none)')}</div>
            <input type="search" id="picker-filter" class="settings-filter" placeholder="Filter by name…" autocomplete="off" />
          </div>
        </header>
        <div class="profile-carousel picker-carousel-grid" id="picker-grid"></div>
        <footer class="profile-picker-footer">
          <button type="button" class="menu-btn" id="picker-cancel">Cancel</button>
          <button type="button" class="menu-btn primary-purple" id="picker-save">Save</button>
        </footer>
      </div>
    `;
    document.body.appendChild(overlay);

    const previewEl = overlay.querySelector('#picker-preview') as HTMLElement;
    const nameEl = overlay.querySelector('#picker-name') as HTMLElement;
    const filterInput = overlay.querySelector('#picker-filter') as HTMLInputElement;
    const gridEl = overlay.querySelector('#picker-grid') as HTMLElement;
    const cancelBtn = overlay.querySelector('#picker-cancel') as HTMLButtonElement;
    const saveBtn = overlay.querySelector('#picker-save') as HTMLButtonElement;

    // Lazy image loading via IntersectionObserver + a scroll-debounce.
    // Kept in sync with the auction-house sell picker — see
    // AuctionHouseView.ts:openAuctionSellPicker for the full rationale.
    // Short version: `root: gridEl` (grid IS the scroll container, so
    // observer works on Safari); no `<img>` created until intersect;
    // 250ms cooldown so fast scrolls don't queue thousands of fetches;
    // first observer dispatch bypasses the cooldown so tiles visible
    // on open hydrate immediately.
    let observer: IntersectionObserver | null = null;
    const HYDRATION_COOLDOWN_MS = 250;
    const pendingHydrations = new WeakMap<HTMLElement, number>();
    const pendingTimeoutIds = new Set<number>();
    let firstDispatch = true;

    const clearAllPending = (): void => {
      for (const id of pendingTimeoutIds) clearTimeout(id);
      pendingTimeoutIds.clear();
    };

    function hydrateSlot(slot: HTMLElement): void {
      const art = slot.querySelector('.profile-carousel-art') as HTMLElement | null;
      if (!art || art.querySelector('img')) return;
      const artUrl = slot.dataset.art;
      if (!artUrl) return;
      const img = document.createElement('img');
      img.src = artUrl;
      img.alt = slot.dataset.cardName ?? '';
      img.draggable = false;
      art.appendChild(img);
    }

    function cancelPendingHydration(slot: HTMLElement): void {
      const t = pendingHydrations.get(slot);
      if (t !== undefined) {
        clearTimeout(t);
        pendingHydrations.delete(slot);
        pendingTimeoutIds.delete(t);
      }
    }

    function schedulePendingHydration(slot: HTMLElement): void {
      if (pendingHydrations.has(slot)) return;
      const t = window.setTimeout(() => {
        pendingHydrations.delete(slot);
        pendingTimeoutIds.delete(t);
        hydrateSlot(slot);
      }, HYDRATION_COOLDOWN_MS);
      pendingHydrations.set(slot, t);
      pendingTimeoutIds.add(t);
    }

    function buildTile(def: CardDef): HTMLElement {
      const tile = document.createElement('div');
      tile.className = 'profile-carousel-tile' + (def.id === selected ? ' is-current' : '');
      if (def.art) tile.dataset.art = def.art;
      tile.dataset.cardName = def.name;

      const art = document.createElement('div');
      art.className = 'profile-carousel-art';
      if (!def.art) {
        art.classList.add('profile-carousel-art-fallback');
        art.textContent = def.name.split(/\s+/).map(w => w[0] ?? '').join('').slice(0, 3).toUpperCase();
      }
      tile.appendChild(art);
      const tileName = document.createElement('div');
      tileName.className = 'profile-carousel-name';
      tileName.textContent = def.name;
      tile.appendChild(tileName);
      tile.addEventListener('click', () => {
        // First tap: make this tile the picker's current selection.
        // Second tap on the same (already-current) tile: open the
        // standard card detail. This lets a mobile player drill
        // into a card's full info without having to drag their
        // finger up to the header preview — useful when comparing
        // candidates deep in the grid.
        if (def.id === selected) {
          void import('./CardDetailView.js').then(m => m.showCardDetail(def.id, catalog));
          return;
        }
        // Local-only selection update. Persisted by the caller iff
        // the modal resolves with a different id than `initialCardId`.
        selected = def.id;
        gridEl.querySelectorAll('.profile-carousel-tile.is-current').forEach(t => t.classList.remove('is-current'));
        tile.classList.add('is-current');
        nameEl.textContent = def.name;
        previewEl.innerHTML = renderCurrentArt(selected, catalog);
      });
      return tile;
    }

    function renderGrid(): void {
      observer?.disconnect();
      clearAllPending();
      gridEl.innerHTML = '';

      const filtered = filterText
        ? allOwned.filter(d => d.name.toLowerCase().includes(filterText.toLowerCase()))
        : allOwned;

      if (filtered.length === 0) {
        gridEl.innerHTML = '<div class="profile-carousel-empty">No cards match your filter.</div>';
        return;
      }

      const frag = document.createDocumentFragment();
      for (const def of filtered) frag.appendChild(buildTile(def));
      gridEl.appendChild(frag);

      firstDispatch = true;
      observer = new IntersectionObserver((entries) => {
        if (firstDispatch) {
          firstDispatch = false;
          for (const entry of entries) {
            if (entry.isIntersecting) hydrateSlot(entry.target as HTMLElement);
          }
          return;
        }
        for (const entry of entries) {
          const slot = entry.target as HTMLElement;
          if (entry.isIntersecting) schedulePendingHydration(slot);
          else cancelPendingHydration(slot);
        }
      }, { root: gridEl, rootMargin: '300px 0px' });
      for (const child of Array.from(gridEl.children)) {
        observer.observe(child);
      }
    }

    filterInput.addEventListener('input', () => {
      filterText = filterInput.value.trim();
      renderGrid();
    });

    function openSelectedDetail(): void {
      if (selected) {
        void import('./CardDetailView.js').then(m => {
          if (selected) m.showCardDetail(selected, catalog);
        });
      }
    }
    previewEl.addEventListener('click', openSelectedDetail);
    previewEl.addEventListener('keydown', (e) => {
      if ((e as KeyboardEvent).key === 'Enter' || (e as KeyboardEvent).key === ' ') {
        e.preventDefault();
        openSelectedDetail();
      }
    });

    function cleanup(): void {
      observer?.disconnect();
      clearAllPending();
      overlay.remove();
      document.removeEventListener('keydown', onKey);
    }

    function close(value: string | null): void {
      cleanup();
      resolve(value);
    }

    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') close(null);
    }
    document.addEventListener('keydown', onKey);

    cancelBtn.addEventListener('click', () => close(null));
    saveBtn.addEventListener('click', () => close(selected));

    renderGrid();
  });
}

function profileNameFor(cardId: string | null, catalog: Map<string, CardDef>): string {
  if (!cardId) return '(none set)';
  return catalog.get(cardId)?.name ?? cardId;
}

/** Just the card's art image — no border, no power/spirit/etc. badges
 *  — so the preview reads as "this is what your portrait will look
 *  like" rather than a redundant copy of the carousel tile. Falls
 *  back to a placeholder when the card has no art set, or to a
 *  "(none)" tile when the player hasn't picked yet. */
function renderCurrentArt(cardId: string | null, catalog: Map<string, CardDef>): string {
  if (!cardId) return '<div class="profile-current-art-empty">No picture</div>';
  const def = catalog.get(cardId);
  if (!def || !def.art) return '<div class="profile-current-art-empty">No art</div>';
  return `<img class="profile-current-art" src="${escapeHtml(def.art)}" alt="${escapeHtml(def.name)}" draggable="false" />`;
}

function formatDate(iso: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/** Coarse duration formatter for the cooldown banner. "23h 41m"
 *  reads better than "23:41:33.027" and doesn't need a ticker — the
 *  player just needs to know "later today" vs. "tomorrow". */
function formatDurationApprox(ms: number): string {
  const totalMin = Math.ceil(ms / 60_000);
  const days = Math.floor(totalMin / 1440);
  const hours = Math.floor((totalMin % 1440) / 60);
  const minutes = totalMin % 60;
  if (days > 0) return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
  if (hours > 0) return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  return `${minutes}m`;
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
